import React, { useContext, useEffect } from "react";
import {
  Typography,
  Grid,
  CardContent,
  Card,
  Box,
  Button,
  Chip,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";

// Icons (lucide-react)
import {
  Flag,
  Package,
  ClipboardList,
  IdCard,
  Users,
  Clock,
  ArrowRight,
} from "lucide-react";

// Custom Components & Hooks
import bgBanner from "../assets/gifs/ExpoBG.gif";
import MonthwiseRegistrationsCard from "./graphs/MonthwiseRegistrationsCard";
import CountrywiseRegistrationsCard2 from "./graphs/CountryWiseRegistrationsCard2";
import IndustrySectorwiseCard from "./graphs/IndustrySectorwiseCard";
import EventDiscoryRegCharts from "./graphs/EventDiscoryRegCharts";
import useApiEp from "../hooks/useApiEp";
import { UserContext } from "../context/UserProvider";
import { USER_ROLES } from "../utils/constants";

const PRIMARY = "#1976d2";

// Reusable style for modern interactive cards
const interactiveCardStyle = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: 3,
  },
};

const Dashboard = () => {
  // Existing Endpoints
  const { user, loading } = useContext(UserContext);
  const navigate = useNavigate();
  const { data } = useApiEp(`/user/get-visitors-count`);
  const { data: exhibitorCount } = useApiEp(`/user/get-exhibitors-count`);
  const { data: countrywiseData } = useApiEp(
    `/user/get-countrywise-registrations`
  );
  const { data: countProducts } = useApiEp(`/admin/count-products`);
  const { data: countOesSubmits } = useApiEp(`/admin/count-oes-submits`);
  const { data: countTeamMembers } = useApiEp(`/admin/count-team-members`);

  // NEW Endpoints for Delegates (Adjust URLs to match your backend)
  const { data: countDelegates } = useApiEp(`/admin/count-delegates`);
  const { data: pendingDelegates } = useApiEp(`/admin/count-pending-delegates`);

  useEffect(() => {
    if (user?.role === USER_ROLES.TD_ADMIN) {
      navigate(`/manage-delegates`);
    }
  }, [user]);
  return (
    <Box sx={{ flexGrow: 1, p: 1 }}>
      <Grid container spacing={3} alignItems="stretch">
        {/* LEFT COLUMN: Main Stats */}
        <Grid item xs={12} md={9} sx={{ display: "flex" }}>
          <Grid container spacing={2} alignItems="stretch" sx={{ flexGrow: 1 }}>
            {/* Welcome Banner */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%", minHeight: 180 }}>
                <CardContent
                  sx={{
                    backgroundImage: `url(${bgBanner})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    color: "white",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(0, 0, 0, 0.4)",
                      zIndex: 1,
                    },
                  }}
                >
                  <Box sx={{ position: "relative", zIndex: 2 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      Welcome!
                    </Typography>
                    <Typography variant="body1" fontWeight="500">
                      ExpoPlanner Manage Admin
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                      Manage Exhibitors, Visitors, Registrations, OES, Floor
                      Plan
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Exhibitors Count */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={interactiveCardStyle}>
                <CardContent
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Exhibitors
                  </Typography>
                  <Typography
                    variant="h3"
                    color="primary.main"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    {exhibitorCount
                      ? exhibitorCount.count?.toLocaleString()
                      : "..."}
                  </Typography>
                  <Box mt="auto">
                    <Link
                      to={"/exhibitors-list"}
                      style={{ textDecoration: "none" }}
                    >
                      <Button size="small" endIcon={<ArrowRight size={16} />}>
                        View List
                      </Button>
                    </Link>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Visitors Count */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={interactiveCardStyle}>
                <CardContent
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    Visitors
                  </Typography>
                  <Typography
                    variant="h3"
                    color="primary.main"
                    fontWeight="bold"
                    sx={{ mb: 2 }}
                  >
                    {data ? data.count?.toLocaleString() : "..."}
                  </Typography>
                  <Box mt="auto">
                    <Link
                      to={"/visitors-list"}
                      style={{ textDecoration: "none" }}
                    >
                      <Button size="small" endIcon={<ArrowRight size={16} />}>
                        View List
                      </Button>
                    </Link>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Minor Stats Row */}
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ ...interactiveCardStyle, bgcolor: "#f8f9fa" }}>
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <Package size={40} color={PRIMARY} style={{ opacity: 0.8 }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {countProducts
                        ? countProducts.count.toLocaleString()
                        : "..."}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Product Uploads
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ ...interactiveCardStyle, bgcolor: "#f0f8ff" }}>
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <Flag size={40} color={PRIMARY} style={{ opacity: 0.8 }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {countrywiseData?.registrations.filter(
                        (item) => item.country
                      )?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Countries
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ ...interactiveCardStyle, bgcolor: "#fffdf0" }}>
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <ClipboardList
                    size={40}
                    color={PRIMARY}
                    style={{ opacity: 0.8 }}
                  />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {countOesSubmits
                        ? countOesSubmits.count.toLocaleString()
                        : "..."}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      OES Submits
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ ...interactiveCardStyle, bgcolor: "#fff5f5" }}>
                <CardContent
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <IdCard size={40} color={PRIMARY} style={{ opacity: 0.8 }} />
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {countTeamMembers
                        ? countTeamMembers.count.toLocaleString()
                        : "..."}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Team Members
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* RIGHT COLUMN: Trade Delegates Panel */}
        <Grid item xs={12} md={3} sx={{ display: "flex" }}>
          <Card
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              borderTop: "4px solid #1976d2",
            }}
          >
            <CardContent
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Users color={PRIMARY} size={28} />
                <Typography variant="h6" fontWeight="bold">
                  Trade Delegates
                </Typography>
              </Box>

              {/* Total Delegates Stat */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight="500"
                >
                  Total Approved
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="text.primary">
                  {countDelegates
                    ? countDelegates.count?.toLocaleString()
                    : "..."}
                </Typography>
              </Box>

              {/* Pending Approval Stat */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#fff3e0",
                  borderRadius: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  border: "1px solid #ffe0b2",
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Clock size={20} color="#e65100" />
                  <Typography
                    variant="body2"
                    color="warning.dark"
                    fontWeight="bold"
                  >
                    Needs Approval
                  </Typography>
                </Box>
                <Chip
                  label={
                    pendingDelegates
                      ? pendingDelegates.count?.toLocaleString()
                      : "..."
                  }
                  color="warning"
                  size="small"
                  sx={{ fontWeight: "bold" }}
                />
              </Box>

              {/* Action Button */}
              <Box mt="auto" pt={2}>
                <Link
                  to={"/manage-delegates"}
                  style={{ textDecoration: "none" }}
                >
                  <Button
                    variant="outlined"
                    fullWidth
                    endIcon={<ArrowRight size={16} />}
                  >
                    Manage Delegates
                  </Button>
                </Link>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* BOTTOM ROW: Charts */}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MonthwiseRegistrationsCard />
            </Grid>
            <Grid item xs={12} md={6}>
              <CountrywiseRegistrationsCard2 />
            </Grid>
            <Grid item xs={12} md={6}>
              <IndustrySectorwiseCard />
            </Grid>
            <Grid item xs={12} md={6}>
              <EventDiscoryRegCharts />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
